import { logger } from "@/utils/logger";

import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatPhoneNumber } from '@/utils/phoneUtils';
import { useCentralizedVehicleInterest } from '@/hooks/useCentralizedVehicleInterest';
import { createStandardizedFormSubmission } from '@/utils/formSubmissionUtils';
import { useFormContext } from '@/contexts/FormContext';
import { useFormAutoClose } from '@/hooks/useFormAutoClose';
import { validateContactFormStep } from '@/utils/formValidation';
import { getStoredInterestData } from '@/utils/interestDataUtils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  FormContainer, 
  FormNavigation, 
  FormStep
} from './shared';

interface ContactFormProps {
  formData: { [key: string]: string };
  onUpdateData: (data: { [key: string]: string }) => void;
  currentStep: number;
  onStepChange: (step: number) => void;
  onSubmit?: (formData: { [key: string]: string }) => void;
  title?: string; // Allow forms to receive custom titles
}

const ContactForm: React.FC<ContactFormProps> = ({
  formData,
  onUpdateData,
  currentStep,
  onStepChange,
  onSubmit,
  title,
}) => {
  const { t, language } = useLanguage();
  const { closeForm } = useFormContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const { getCompleteInterestData } = useCentralizedVehicleInterest();
  const [storedInterest, setStoredInterest] = useState<any>(null);
  const [localFormData, setLocalFormData] = useState(formData);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const totalSteps = 3; // Name + Phone (step 0), Email (step 1), Question (step 2)
  
  // Uncontrolled input refs
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const questionInputRef = useRef<HTMLTextAreaElement | null>(null);
  
  // Sync local state when formData changes but stop once user starts typing
  useEffect(() => {
    if (!hasUserEdited) {
      setLocalFormData(formData);
    }
  }, [formData, hasUserEdited]);
  
  // Load interest data on mount and when form opens
  useEffect(() => {
    const loadInterestData = async () => {
      try {
        const data = await getCompleteInterestData();
        setStoredInterest(data);
      } catch (error) {
        console.error('ContactForm - Error loading interest data:', error);
      }
    };
    
    loadInterestData();
  }, [getCompleteInterestData]);
  
  // Use the extracted auto-close hook
  useFormAutoClose(isSubmitting);

  // Get prefilled interest text from stored data
  const getPrefilledInterest = () => {
    logger.log('ContactForm - storedInterest:', storedInterest);
    if (!storedInterest) return '';
    
    const parts = [];
    
    // Add summary and message if available
    if (storedInterest.summary) parts.push(storedInterest.summary);
    if (storedInterest.message) parts.push(storedInterest.message);
    
    // Only add vehicle info if no summary/message already contains it
    if (storedInterest.vehicle && parts.length === 0) {
      const vehicle = storedInterest.vehicle;
      const vehicleText = `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim();
      if (vehicleText) {
        parts.push(vehicleText);
      }
    }
    
    logger.log('ContactForm - prefilled parts:', parts);
    return parts.join(' - ');
  };

  // Auto-prefill question field when navigating to step 2 if it's empty and we have stored interest
  useEffect(() => {
    if (currentStep === 2 && !formData.question) {
      const prefilledValue = getPrefilledInterest();
      logger.log('ContactForm - prefilledValue:', prefilledValue);
      if (prefilledValue) {
        onUpdateData({ question: prefilledValue });
      }
    }
  }, [currentStep, formData.question, storedInterest]);

  const handleClearQuestion = () => {
    if (questionInputRef.current) {
      questionInputRef.current.value = '';
    }
    setLocalFormData(prev => ({ ...prev, question: '' }));
    onUpdateData({ question: '' });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (!hasUserEdited) {
      setHasUserEdited(true);
    }

    // Force re-render so isStepValid updates for uncontrolled inputs
    setLocalFormData(prev => ({ ...prev, [name]: value }));

    // Persist into FormContext (merge)
    onUpdateData({ [name]: value });

    // Clear field error as user edits
    if (validationErrors.has(name)) {
      setValidationErrors(prev => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
  };

  const handleNext = () => {
    // Use refs first, fallback to localFormData for prefilled values
    const name = nameInputRef.current?.value || localFormData.name || '';
    const phone = phoneInputRef.current?.value || localFormData.phone || '';
    const email = emailInputRef.current?.value || localFormData.email || '';
    const question = questionInputRef.current?.value || localFormData.question || '';
    
    if (!isStepValid()) {
      // Mark invalid fields
      const errors = new Set<string>();
      if (currentStep === 0) {
        if (!name.trim()) errors.add('name');
        if (!phone.trim()) errors.add('phone');
      } else if (currentStep === 1) {
        if (!email.trim() || !email.includes('@')) errors.add('email');
      } else if (currentStep === 2) {
        if (!question.trim()) errors.add('question');
      }
      setValidationErrors(errors);
      return;
    }
    setValidationErrors(new Set());
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      onStepChange(nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      onStepChange(prevStep);
    }
  };

  const handleFormSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Use refs first, fallback to localFormData for prefilled values
      const name = nameInputRef.current?.value || localFormData.name || '';
      const phone = phoneInputRef.current?.value || localFormData.phone || '';
      const email = emailInputRef.current?.value || localFormData.email || '';
      const question = questionInputRef.current?.value || localFormData.question || '';
      const interest = formData.interest || '';
      
      // Get stored interest data
      const storedInterest = await getStoredInterestData();
      
      // Create contact-specific message
      const contactMessage = language === 'fr'
        ? `Demande de contact - ${name} a soumis un formulaire de contact${question ? ` avec question: ${question}` : ''}. Contact: ${phone} / ${email}`
        : `Contact Request - ${name} has submitted a contact form${question ? ` with question: ${question}` : ''}. Contact: ${phone} / ${email}`;
      
      const contactInterestData = {
        ...storedInterest,
        message: contactMessage,
        department: storedInterest?.department || 'New'
      };
      
      const submissionData = createStandardizedFormSubmission(
        {
          name,
          phone,
          email,
          interest,
          question
        },
        'contact',
        formatPhoneNumber,
        contactInterestData
      );

      if (onSubmit) {
        await onSubmit(submissionData);
        closeForm();
      }
      
      onUpdateData({ name, phone, email, interest, question });
    } catch (error) {
      console.error('ContactForm - Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    // Use refs first, fallback to localFormData for prefilled values
    const name = nameInputRef.current?.value || localFormData.name || '';
    const phone = phoneInputRef.current?.value || localFormData.phone || '';
    const email = emailInputRef.current?.value || localFormData.email || '';
    const question = questionInputRef.current?.value || localFormData.question || '';
    
    switch (currentStep) {
      case 0:
        return name.trim().length > 0 && phone.trim().length > 0;
      case 1:
        return email.trim().length > 0 && email.includes('@');
      case 2:
        return question.trim().length > 0;
      default:
        return false;
    }
  };

  const renderStep = () => {
    
    switch (currentStep) {
      case 0:
        return (
          <FormStep>
            {/* Hidden Interest field */}
            <input type="hidden" name="interest" value={localFormData.interest || ''} />
            
            {/* Hidden topcarid field */}
            <input 
              id="topcarid" 
              name="topcarid" 
              type="hidden" 
              value={(() => {
                try {
                  const topCarIdElement = document.getElementById('topcarid') as HTMLInputElement;
                  return topCarIdElement?.value ? `Vehicle ID: ${topCarIdElement.value}` : '';
                } catch {
                  return '';
                }
              })()}
            />
            
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="name" className={cn("text-xs", validationErrors.has('name') ? "text-red-500" : "text-gray-700")}>{t('form.fullName')}</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  defaultValue={localFormData.name || ''}
                  ref={nameInputRef}
                  onChange={handleInputChange}
                  placeholder={t('form.fullName')}
                  className="h-7 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="phone" className={cn("text-xs", validationErrors.has('phone') ? "text-red-500" : "text-gray-700")}>{t('form.phone')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  defaultValue={localFormData.phone || ''}
                  ref={phoneInputRef}
                  onChange={handleInputChange}
                  placeholder="(555) 555-1212"
                  className="h-7 text-sm"
                />
              </div>
            </div>
          </FormStep>
        );
      case 1:
        return (
          <FormStep>
            <div className="space-y-1">
              <Label htmlFor="email" className={cn("text-xs", validationErrors.has('email') ? "text-red-500" : "text-gray-700")}>{t('form.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                defaultValue={localFormData.email || ''}
                ref={emailInputRef}
                onChange={handleInputChange}
                placeholder="exemple@email.com"
                className="h-7 text-sm"
              />
            </div>
          </FormStep>
        );
      case 2:
        return (
          <FormStep>
            <div className="space-y-1">
              <Label htmlFor="question" className={cn("text-xs", validationErrors.has('question') ? "text-red-500" : "text-gray-700")}>{t('form.question_interest')}</Label>
              <div className="relative">
                {questionInputRef.current?.value && (
                  <button
                    type="button"
                    onClick={handleClearQuestion}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 z-10"
                    title="Clear field"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                <Textarea
                  id="question"
                  name="question"
                  defaultValue={localFormData.question || ''}
                  ref={questionInputRef}
                  onChange={handleInputChange}
                  placeholder={t('form.question_interest_placeholder')}
                  className="min-h-[40px] resize-y"
                />
              </div>
            </div>
          </FormStep>
        );
      default:
        return null;
    }
  };

  // ContactForm: Render - currentStep removed for production

  return (
    <FormContainer className="p-1.2">
      <div className="mb-2">
        {renderStep()}
      </div>
      
      <FormNavigation
        currentStep={currentStep}
        totalSteps={totalSteps}
        isStepValid={isStepValid}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
      />
    </FormContainer>
  );
};

export default ContactForm;
